import { createSlice, Dispatch } from "@reduxjs/toolkit"; // PayloadAction

import enUS from "antd/lib/locale/en_US";
import ruRU from "antd/lib/locale/ru_RU";
import trTR from "antd/lib/locale/tr_TR";

// import { message } from "antd";
import type {Tuser} from "../types/easyTypes.ts";
import type Istate from "../types/Istate.d.ts";
import type Iconfig from "../types/Iconfig.d.ts";
import type {TappSection, Tapp, TelementState, TuiState, TappJSON} from "../types/uiapp.d.ts";

import fnc from "./fnc.ts";
import dict from "../../../../dict/app";

const {rz, $} = window;

let lastTimeError = 0;

const detectTheme = () => {
  const theme = $("html").attr("data-theme");
  return theme;
};

const detectCompact = () => {
  const attrcompact = $("html").attr("data-compact");
  return (attrcompact === "true");
};

const detectIconsTitle = () => {
  const attrIconsTitle = $("html").attr("data-icons-title");
  return (attrIconsTitle === "true");
};

const detectLng = () => {
  let lng = "en";
  if (~document.title.indexOf("[RU]")) { lng = "ru" }
  else if (~document.title.indexOf("[TR]")) { lng = "tr" }
  return lng;
};

const detectMobile = () => {
  const isMobile = rz.isMobileDevice(navigator.userAgent);
  return isMobile;
};

const getAppLocale = () => {
  const lng = detectLng();
  if (lng === "ru") { return ruRU }
  if (lng === "tr") { return trTR }
  return enUS;
};

const getFullPathFromKey = (path:string, appstate:TuiState) => {
  let fullPath = path;
  if (!~fullPath.indexOf("/")) {
    let fnd = false; const allStateKeys = rz.objKeys(appstate);
    for (let i = 0; i < allStateKeys.length; i++) {
      // eslint-disable-next-line prefer-destructuring
      if (appstate[allStateKeys[i]].key === path) { fnd = true; fullPath = appstate[allStateKeys[i]].fullPath; break }
    }
    if (!fnd) { return path }
  }
  return fullPath;
};

const initialState = {
  cfg: {
    appname: "", version: "",
    links: {upload: "", avatar: "", srvbgUrl: "", portalUrl: "", devUrl: "", sharepointURL: "", owaAddress: ""},
    ad: {usersLoginEndsWith: ""}, skud: {}, safe: false
  },
  apps: [],
  mainmenu: [],
  usermenu: [],
  desktopApps: [],
  isMobile: detectMobile(),
  globalMask: false,
  desktopHeight: 0,
  theme: detectTheme(),
  compact: detectCompact(),
  showDesktopIconsTitle: detectIconsTitle(),
  lng: detectLng(),
  dct: dict[detectLng()],
  locale: getAppLocale(),
  user: {name: null, name_en: null, name_ru: null, groups: null, login: null},
  personalsettings: [],
  socketActions: [],
  uiState: {},
  uiJSON: {},
  chatgpt: {enabled: false, token: "", proxyaddress: ""},
  fixAppsInTheDesktop: true
} as Istate;

const appReducer = createSlice({
  name: "ar", initialState,
  reducers: {
    setConfig: (state, action:{payload:Iconfig}) => {
      // Current user
      if (action.payload.user && action.payload.user.login) { state.user = action.payload.user }
      else { state.user = {name: null, name_en: null, name_ru: null, login: null, groups: null} }
      // Other settings
      // const cpo = rz.objClone(e.payload); if (rz.objHasKey(cpo, "user")) delete cpo.user;
      state.cfg = action.payload;
    },
    setLng: (state, e:{payload:string}) => {
      state.lng = e.payload || "en"; dict.set(e.payload || "en"); state.dct = dict[e.payload || "en"];
      state.locale = (e.payload === "ru" ? ruRU : enUS);
    },
    setTheme: (state, e:{payload:string}) => {
      state.theme = e.payload || "dark";
    },
    setCompact: (state, e:{payload:boolean}) => {
      state.compact = e.payload || false;
    },
    setShowDesktopIconsTitle: (state, e:{payload:boolean}) => {
      state.showDesktopIconsTitle = e.payload || false;
    },
    setDesktopType: (state, e:{payload:boolean}) => {
      state.isMobile = e.payload || false;
    },
    setGlobalMask: (state, e:{payload:boolean}) => {
      state.globalMask = e.payload || false;
    },
    setDesktopHeight: (state, e:{payload:number}) => {
      state.desktopHeight = e.payload || 512;
    },
    setUser: (state, e:{payload:Tuser}) => {
      if (e.payload && e.payload.login) { state.user = e.payload } // state.cfg.user = e.payload
      else { state.user = {name: null, name_en: null, name_ru: null, login: null, groups: null} } // state.cfg.user = {name: null, name_en: null, name_ru: null, login: null, groups: null}
    },
    pushSocketAction: (state, e:{payload:{event:string, data:any, key:number}}) => {
      if (e.payload && e.payload.event && rz.isString(e.payload.event) && e.payload.data && e.payload.key) {
        state.socketActions.push({event: e.payload.event, data: e.payload.data, key: e.payload.key});
      }
    },
    clearSocketActionsReducer: (state, e:{payload:number | undefined}) => {
      if (e && !rz.isUndefinedOrNull(e.payload)) {
        state.socketActions.splice((e.payload as number), 1)
      }
      else { state.socketActions = [] }
    },
    setAllApps: (state, e:{payload:{apps: Tapp[] | undefined, usermenu: Tapp[] | undefined, mainmenu: TappSection[] | undefined, desktopApps: Tapp[] | undefined}}) => {
      // @ts-ignore
      if (e.payload && rz.isArray(e.payload.apps) && e.payload.apps.length) {
        // @ts-ignore
        state.apps = e.payload.apps; state.mainmenu = e.payload.mainmenu; state.usermenu = e.payload.usermenu; state.desktopApps = e.payload.desktopApps;
      }
      else { state.apps = []; state.mainmenu = []; state.usermenu = []; state.desktopApps = [] }
    },
    setDesktopApps: (state, e:{payload:Tapp[] | undefined}) => {
      if (e.payload && rz.isArray(e.payload) && e.payload.length) {
        state.desktopApps = e.payload;
      }
      else { state.apps = []; state.mainmenu = []; state.usermenu = []; state.desktopApps = [] }
    },
    setXMLtoApp: (state, e:{payload:Tapp}) => {
      if (e.payload && e.payload.key) {
        const i = rz.arrFindObj(state.apps, {key: e.payload.key});
        if (~i) {
          if (e.payload.templateXML) { state.apps[i].templateXML = e.payload.templateXML } else { state.apps[i].templateXML = "" }
          if (e.payload.scripts) { state.apps[i].scripts = e.payload.scripts } else { state.apps[i].scripts = [] }
        }
      }
    },
    setAppState: (state, e:{payload:{appkey:string, newState:TuiState}}) => { // State of full app
      if (e.payload && e.payload.appkey && rz.isObject(e.payload.newState)) {
        state.uiState[e.payload.appkey] = e.payload.newState;
      }
    },
    changeAppState: (state, e:{payload:{appkey:string, fullPath:string | Array<string>, newState:TelementState | Array<TelementState>}}) => { // State of an element
      // console.log("changeAppState(" + e.payload.appkey + ") -> " + e.payload.fullPath);
      if (e.payload && e.payload.appkey && e.payload.fullPath) {
        if (!state.uiState[e.payload.appkey]) { state.uiState[e.payload.appkey] = {} }
        // console.log({fullPath: e.payload.fullPath, newState: e.payload.newState});
        if (rz.isString(e.payload.fullPath) && rz.isObject(e.payload.newState)) {
          const fullPathFromKey = getFullPathFromKey(e.payload.fullPath as string, state.uiState[e.payload.appkey]);
          state.uiState[e.payload.appkey][fullPathFromKey] = {...state.uiState[e.payload.appkey][fullPathFromKey], ...e.payload.newState} as TelementState;
        }
        else if (rz.isArray(e.payload.fullPath) && rz.isArray(e.payload.newState) && (e.payload.newState as Array<TelementState>).length === (e.payload.fullPath as Array<string>).length) {
          (e.payload.fullPath as Array<string>).forEach((fp:string, fi:number) => {
            const fpfk = getFullPathFromKey(fp as string, state.uiState[e.payload.appkey]);
            state.uiState[e.payload.appkey][fpfk] = ({...state.uiState[e.payload.appkey][fpfk], ...e.payload.newState[fi]} as TelementState);
          });
        }
      }
    },
    // Experimental function:
    modifyAGgridRowsReducer: (state, e:{payload:{
      appkey:string,
      gridPath:string,
      newRows?:Array<{[key:string]:any}>,
      editRows?:Array<{index:number, row:{[key:string]:any}}>,
      deleteRows?:Array<number>,
      insBeforeIndex?:number
    }}) => {
      if (!e.payload || !e.payload.appkey || !e.payload.gridPath || !state.uiState[e.payload.appkey]) { return }
      const {appkey, gridPath, editRows, deleteRows, newRows, insBeforeIndex} = e.payload;
      const gridFullPath = getFullPathFromKey(gridPath, state.uiState[appkey]);
      const aggrid = state.uiState[appkey][gridFullPath]; if (!aggrid.data) { aggrid.data = [] }
      // Debug:
      // if (state?.cfg?.isDev) console.log({appkey, gridFullPath, newRows, editRows, deleteRows, aggridData: aggrid.data, insBeforeIndex});

      if ((!newRows || !newRows.length) && (!editRows || !editRows.length) && (!deleteRows || !deleteRows.length)) { return }
      if (newRows && newRows.length) {
        newRows.forEach((rr) => {
          if (insBeforeIndex === 0 || insBeforeIndex) {
            aggrid.data?.splice(insBeforeIndex, 0, {...rr});
          }
          else {
            aggrid.data?.push({...rr});
          }
        });
      }
      if (deleteRows && deleteRows.length) {
        const dsorted = deleteRows.sort((a, b) => (a > b ? -1 : (a < b ? 1 : 0))); // Удалять нужно с хвоста -> вверх по массиву (иначе порядковые индексы строк полетят)
        dsorted.forEach((rr) => { aggrid.data?.splice(rr, 1) }); // rr is number (row-index)
      }
      if (editRows && editRows.length) {
        // @ts-ignore
        editRows.forEach((rr) => { aggrid.data[rr.index] = {...rr.row} }); // rr.index:number, rr.row:object
      }
    },
    clearAppState: (state, e:{payload:string}) => {
      if (e.payload) { // appkey
        state.uiState[e.payload] = {};
      }
    },
    setAppJSON: (state, e:{payload:{appkey:string, newJSON:any}}) => { // State of full app
      if (e.payload && e.payload.appkey && (rz.isObject(e.payload.newJSON) || rz.isArray(e.payload.newJSON))) {
        state.uiJSON[e.payload.appkey] = e.payload.newJSON;
      }
    },
    changeAppJSON: (state, e:{payload:{appkey:string, jsonBlockPath:string | Array<string>, newJSONblock:{[key:string]:any} | Array<{[key:string]:any}>}}) => { // App's JSON
      if (e.payload && e.payload.appkey && e.payload.jsonBlockPath && e.payload.newJSONblock) {
        if (!state.uiJSON[e.payload.appkey]) { state.uiJSON[e.payload.appkey] = {} }
        if (rz.isString(e.payload.jsonBlockPath) && rz.isObject(e.payload.newJSONblock)) {
          const correctJSONpath = ((e.payload.jsonBlockPath as string).startsWith(".elements") ? (e.payload.jsonBlockPath as string).replace(".elements", "") : e.payload.jsonBlockPath);
          const JSONslice = rz.objByString(state.uiJSON[e.payload.appkey], (correctJSONpath as string), true);
          // @ts-ignore
          if (JSONslice && e.payload.newJSONblock.elements) {
            // @ts-ignore
            try { JSONslice.elements = e.payload.newJSONblock.elements } catch (err) { console.log(err) }
          }
          // state.uiJSON[e.payload.appkey][e.payload.jsonBlockPath as string] = e.payload.newJSONblock as {[key:string]:any};
        }
        else if (rz.isArray(e.payload.jsonBlockPath) && rz.isArray(e.payload.newJSONblock) && (e.payload.newJSONblock as Array<{[key:string]:any}>).length === (e.payload.jsonBlockPath as Array<string>).length) {
          (e.payload.jsonBlockPath as Array<string>).forEach((fp:string, fi:number) => {
            const correctJSONpath = ((fp as string).startsWith(".elements") ? (fp as string).replace(".elements", "") : fp);
            const JSONslice = rz.objByString(state.uiJSON[e.payload.appkey], correctJSONpath, true);
            if (JSONslice && e.payload.newJSONblock[fi].elements) {
              // @ts-ignore
              try { JSONslice.elements = e.payload.newJSONblock[fi].elements } catch (err) { console.log(err) }
            }
          });
        }
      }
    },
    clearAppJSON: (state, e:{payload:string}) => {
      if (e.payload) { // appkey
        state.uiJSON[e.payload] = {};
      }
    },
    setDesktopFixed: (state, e:{payload:boolean}) => {
      state.fixAppsInTheDesktop = e.payload;
    }
  }
});

// Export main reducer:
// ===============================
export default appReducer.reducer;
// ===============================

// Export simple actions (fast-functions):
export const {
  setConfig, setDesktopType, setGlobalMask, setDesktopHeight,
  setLng, setTheme, setCompact, setShowDesktopIconsTitle, setUser,
  pushSocketAction, clearSocketActionsReducer,
  setAllApps, setDesktopApps, setXMLtoApp, setAppState, changeAppState, clearAppState,
  setAppJSON, changeAppJSON, clearAppJSON, modifyAGgridRowsReducer, setDesktopFixed
} = appReducer.actions;

// Current state getters:
export const getConfig = (state:{ar:Istate}) => state.ar.cfg;
export const getLng = (state:{ar:Istate}) => state.ar.lng;
export const getDict = (state:{ar:Istate}) => state.ar.dct;
export const getDesktopType = (state:{ar:Istate}) => state.ar.isMobile;
export const getGlobalMask = (state:{ar:Istate}) => state.ar.globalMask;
export const getDesktopHeight = (state:{ar:Istate}) => state.ar.desktopHeight;
export const getLocale = (state:{ar:Istate}) => state.ar.locale;
export const getTheme = (state:{ar:Istate}) => state.ar.theme;
export const getCompact = (state:{ar:Istate}) => state.ar.compact;
export const getShowDesktopIconsTitle = (state:{ar:Istate}) => state.ar.showDesktopIconsTitle;
export const getUser = (state:{ar:Istate}) => state.ar.user;
export const getSocketActions = (state:{ar:Istate}) => state.ar.socketActions;
export const getAllApps = (state:{ar:Istate}) => state.ar.apps;
export const getMenuApps = (state:{ar:Istate}) => state.ar.mainmenu;
export const getUserMenuApps = (state:{ar:Istate}) => state.ar.usermenu;
export const getDesktopApps = (state:{ar:Istate}) => state.ar.desktopApps;
export const getAppsState = (state:{ar:Istate}) => state.ar.uiState;
export const getAppState = (appkey:string) => (state:{ar:Istate}) => (state.ar.uiState[appkey] || <TuiState>{});
export const getElementState = (appkey:string, fullPath:string) => (state:{ar:Istate}) => ((state.ar.uiState[appkey] ? state.ar.uiState[appkey][fullPath] : <TelementState>{}) || <TelementState>{});
export const getAppJSON = (appkey:string) => (state:{ar:Istate}) => (state.ar.uiJSON[appkey] || <TappJSON>{});
export const getDesktopFixed = (state:{ar:Istate}) => (state.ar.fixAppsInTheDesktop);
export const getSafePtr = (state:{ar:Istate}) => (state.ar.cfg.safe);

// ===============================
// There are async functions next:
// ===============================

export const onChangeLng = (v:string, preventDoubleCalling?:boolean) => async (dispatch:Dispatch<any>, getState) => {
  const {ar} = getState();
  const lng = await fnc.changeLanguage(v || "en");
  if (!lng) return;
  dispatch(setLng(lng));
  // If we are in User Settings-Page:
  if (!preventDoubleCalling && ar?.uiState?.usersettings) {
    // @ts-ignore
    dispatch(changeAppState({appkey: "usersettings", fullPath: "interfaceLanguage", newState: {value: lng}}));
  }
  return lng;
};

export const onChangeTheme = (switcher:boolean, preventDoubleCalling?:boolean) => async (dispatch:Dispatch<any>, getState) => {
  const {ar} = getState();
  const theme = await fnc.changeTheme(switcher ? "dark" : "light");
  dispatch(setTheme(theme));
  if (!preventDoubleCalling && ar?.uiState?.usersettings) {
    // @ts-ignore
    dispatch(changeAppState({appkey: "usersettings", fullPath: "interfaceTheme", newState: {value: switcher}}));
  }
  return theme;
};

export const onChangeCompact = (switcher:boolean, preventDoubleCalling?:boolean) => async (dispatch:Dispatch<any>, getState) => {
  const {ar} = getState();
  const compact = await fnc.changeCompact(switcher);
  dispatch(setCompact(compact));
  if (!preventDoubleCalling && ar?.uiState?.usersettings) {
    // @ts-ignore
    dispatch(changeAppState({appkey: "usersettings", fullPath: "interfaceCompact", newState: {value: switcher}}));
  }
  return compact;
};

export const onChangeShowDesktopIconsTitle = (switcher:boolean, preventDoubleCalling?:boolean) => async (dispatch:Dispatch<any>, getState) => {
  const {ar} = getState();
  const showDesktopIconsTitle = await fnc.changeShowDesktopIconsTitle(switcher);
  dispatch(setShowDesktopIconsTitle(showDesktopIconsTitle));
  if (!preventDoubleCalling && ar?.uiState?.usersettings) {
    // @ts-ignore
    dispatch(changeAppState({appkey: "usersettings", fullPath: "showDesktopIconsTitle", newState: {value: switcher}}));
  }
  return showDesktopIconsTitle;
};

export const reloadConfigAndApps = (force:boolean = false) => async (dispatch:Dispatch<any>, getState) => {
  // Prevent double settings loading if time from last request < 3 sec
  const curTime = (new Date()).getTime(); if (!force && rz.isNumber(fnc.config.cfgtime) && (curTime - fnc.config.cfgtime) < 3000) { return true }
  fnc.config.cfgtime = curTime;
  const {ar} = getState();
  const cfg = await fnc.getSettings();
  dispatch(setConfig(cfg));
  if (cfg && cfg.err) {
    if ((curTime - lastTimeError) > 1000) { window.antd.notification.error({message: cfg.err, duration: 8, placement: "bottomRight"}) }
    lastTimeError = curTime;
    return false;
  }

  await rz.delay(300);
  const res = await fnc.loadAllApps();
  dispatch(setAllApps({apps: res.apps, mainmenu: res.mainmenu, usermenu: res.usermenu, desktopApps: res.desktopApps}));
  // Change app params according new params from user.personalsettings:
  if (cfg.lng !== ar.lng) {
    dispatch(setLng(cfg.lng));
    fnc.config.lng = cfg.lng;
    dict.set(cfg.lng);
    // Change head and meta tags
    $("meta[name='description']").attr("content", dict[cfg.lng || "en"].p.prgtitle);
    $("html").attr("lang", cfg.lng || "en");
    document.title = dict[cfg.lng || "en"].p.prgtitle;
  }
  if (cfg.theme !== ar.theme) {
    dispatch(setTheme(cfg.theme || "dark"));
    fnc.config.theme = cfg.theme || "dark";
    $("html").attr("data-theme", cfg.theme || "dark");
  }
  if (cfg.compact !== ar.compact) {
    dispatch(setCompact(cfg.compact || false));
    fnc.config.compact = cfg.compact || false;
    $("html").attr("data-compact", String(cfg.compact || false));
  }

  if (fnc.config?.isDev) {
    console.log({configFromServer: cfg, apps: res.apps, mainmenu: res.mainmenu, usermenu: res.usermenu, desktopApps: res.desktopApps});
    if (res.err) console.log(res.err);
  }
  return Boolean(cfg.user?.login);
};

export const modifyAGgridRows = (
  {appkey, gridPath, newRows, editRows, deleteRows, insBeforeIndex}
  :{appkey:string, gridPath:string, newRows?:Array<{[key:string]:any}>, editRows?:Array<{[key:string]:any}>, deleteRows?:Array<{[key:string]:any}>, insBeforeIndex?:number}
) => (dispatch, getState) => {
  const {ar} = getState();
  const gridFullPath = getFullPathFromKey(gridPath, ar.uiState[appkey]);

  if (!appkey || !gridFullPath || !ar.uiState[appkey] || !ar.uiState[appkey][gridFullPath] || !ar.uiState[appkey][gridFullPath].data) { return }
  const aggrid = ar.uiState[appkey][gridFullPath].data;
  // Debug:
  // console.log({appkey, gridPath, gridFullPath, newRows, editRows, deleteRows, aggrid, ar});

  if ((!newRows || !newRows.length) && (!editRows || !editRows.length) && (!deleteRows || !deleteRows.length)) { return }
  const nr:Array<{[key:string]:any}> = [], dr:Array<number> = [], er:Array<{index:number, row:{[key:string]:any}}> = [];
  if (newRows && newRows.length) {
    newRows.forEach((rr) => {
      if (rr._id && !~rz.arrFindObj((aggrid as any[]), {_id: rr._id}, ["_id"])) { nr.push({...rr, key: rr.key || rr._id}) }
      else if ((rr.key || rr.key === 0) && !~rz.arrFindObj((aggrid as any[]), {key: rr.key}, ["key"])) { nr.push({...rr, _id: rr._id || rr.key}) }
    });
  }
  if (deleteRows && deleteRows.length) {
    deleteRows.forEach((rr) => {
      if (rr._id) {
        const delIndex = rz.arrFindObj((aggrid as any[]), {_id: rr._id}, ["_id"]);
        if (~delIndex) { dr.push(delIndex) }
      }
      else if (rr.key || rr.key === 0) {
        const delIndex = rz.arrFindObj((aggrid as any[]), {key: rr.key}, ["key"]);
        if (~delIndex) { dr.push(delIndex) }
      }
    });
  }
  if (editRows && editRows.length) {
    editRows.forEach((rr) => {
      if (rr._id) {
        const editIndex = rz.arrFindObj((aggrid as any[]), {_id: rr._id}, ["_id"]);
        if (~editIndex) { er.push({index: editIndex, row: {...rr, key: rr.key || rr._id}}) }
      }
      else if (rr.key || rr.key === 0) {
        const editIndex = rz.arrFindObj((aggrid as any[]), {key: rr.key}, ["key"]);
        if (~editIndex) { er.push({index: editIndex, row: {...rr, _id: rr._id || rr.key}}) }
      }
    });
  }
  // Debug:
  // console.log({nr, er, dr});
  dispatch(modifyAGgridRowsReducer({appkey, gridPath: gridFullPath, newRows: nr, editRows: er, deleteRows: dr, insBeforeIndex}));
};

export const clearSocketAction = (keyTime) => (dispatch, getState) => {
  const {ar} = getState();
  if (!ar.socketActions || !ar.socketActions.length) { return }
  const index = rz.arrFindObj(ar.socketActions, {key: keyTime});
  if (~index) { dispatch(clearSocketActionsReducer(index)) }
};
